import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileDown, FileText } from "lucide-react";

const PNA_LABEL = {
  fresh_unit: "Fresh Unit",
  damaged_set: "Damaged Set",
  refurbished_set: "Refurbished Set",
  market_purchase: "Market Purchase",
};

export default function PartApprovalReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filters, setFilters] = useState({
    start: "", end: "", status: "", technician: "", brand: "", part: "", approver: "",
  });

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/reports/part-approval", { params });
      setRows(data.rows || []);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* initial */ /* eslint-disable-next-line */ }, []);

  const download = (fmt) => {
    const qs = new URLSearchParams({ fmt, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) }).toString();
    downloadBlob({
      url: `/reports/part-approval?${qs}`,
      filename: `part_approval.${fmt}`,
    });
  };

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  if (err) return (
    <Card data-testid="part-approval-error"><CardContent className="p-6">
      <div className="text-rose-700 font-semibold">Could not load Part Approval Report</div>
      <div className="text-xs text-slate-600 mt-1">{String(err)}</div>
    </CardContent></Card>
  );

  return (
    <div className="space-y-5" data-testid="part-approval-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Part Approval Report</h1>
        <p className="text-sm text-slate-500 mt-1">
          Parts requested, approved or rejected, with approver, source, dispatch status, and PNA decision.
        </p>
      </div>

      <Card><CardContent className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Fld label="From"><Input type="date" value={filters.start} onChange={(e) => update("start", e.target.value)} data-testid="filter-start" /></Fld>
          <Fld label="To"><Input type="date" value={filters.end} onChange={(e) => update("end", e.target.value)} data-testid="filter-end" /></Fld>
          <Fld label="Status">
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm" value={filters.status} onChange={(e) => update("status", e.target.value)} data-testid="filter-status">
              <option value="">All</option>
              <option>Parts Pending</option>
              <option>PNA</option>
              <option>Parts Approved</option>
              <option>Parts in Transit</option>
              <option>Parts Sent</option>
              <option>Received by Technician</option>
            </select>
          </Fld>
          <Fld label="Technician"><Input value={filters.technician} onChange={(e) => update("technician", e.target.value)} placeholder="exact name" data-testid="filter-tech" /></Fld>
          <Fld label="Brand"><Input value={filters.brand} onChange={(e) => update("brand", e.target.value)} placeholder="exact brand" data-testid="filter-brand" /></Fld>
          <Fld label="Part"><Input value={filters.part} onChange={(e) => update("part", e.target.value)} placeholder="substring" data-testid="filter-part" /></Fld>
          <Fld label="Approver"><Input value={filters.approver} onChange={(e) => update("approver", e.target.value)} placeholder="exact name" data-testid="filter-approver" /></Fld>
        </div>
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={load} className="bg-slate-900 text-white" data-testid="apply-filters">Apply</Button>
          <Button size="sm" variant="outline" onClick={() => { setFilters({ start: "", end: "", status: "", technician: "", brand: "", part: "", approver: "" }); setTimeout(load, 0); }}>Clear</Button>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => download("xlsx")} data-testid="export-xlsx"><FileDown size={14} className="mr-1" />Excel</Button>
          <Button size="sm" variant="outline" onClick={() => download("pdf")} data-testid="export-pdf"><FileText size={14} className="mr-1" />PDF</Button>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <Th>Complaint</Th><Th>Customer</Th><Th>Brand/Product/Model</Th>
                <Th>Part Req.</Th><Th>Part Code</Th><Th align="right">Qty</Th>
                <Th>Req. By</Th><Th>Req. Date</Th>
                <Th>Approved By</Th><Th>Approval Date</Th>
                <Th>Status</Th><Th>Dispatch</Th>
                <Th>PNA</Th><Th>Final Source</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={14} className="text-center py-12 text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={14} className="text-center py-12 text-slate-400">No rows.</td></tr>}
              {rows.map((r, i) => (
                <tr key={`${r.complaint_number}-${i}`} data-testid={`pa-row-${r.complaint_number}`}>
                  <td className="font-mono">{r.complaint_number}</td>
                  <td>{r.customer}</td>
                  <td className="text-slate-700">{r.brand}<div className="text-xs text-slate-500">{r.product} {r.model}</div></td>
                  <td className="text-slate-700">{r.part_requested || r.pna_part_details || "—"}</td>
                  <td className="font-mono">{r.part_code || "—"}</td>
                  <td className="text-right font-mono">{r.quantity || "—"}</td>
                  <td>{r.requested_by || "—"}</td>
                  <td className="text-xs">{r.requested_date || "—"}</td>
                  <td>{r.approved_by || "—"}{r.approved_by_role && <div className="text-xs text-slate-500">({r.approved_by_role})</div>}</td>
                  <td className="text-xs">{r.approval_date || "—"}</td>
                  <td>{r.status}</td>
                  <td>{r.dispatch_status}</td>
                  <td>{r.pna_status || "—"}</td>
                  <td>{PNA_LABEL[r.final_source] || r.final_source || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold`}>{children}</th>;
}
